import { NODE_WIDTH, NODE_HEIGHT } from '../utils/canvasUtils';
import {
  VIEWPORT_PADDING_RATIO,
  VIEWPORT_HYSTERESIS_EXTRA,
  SCREEN_CARD_PADDING_RATIO,
} from './constants';
import { calculateVisibleBounds, isNodeInBounds } from './geometry';

function allTiers(tasks) {
  const all = new Set(tasks.map((t) => t.id));
  return { mounted: all, full: all, immediate: all };
}

/**
 * Mounted ids stay in the padded band (with hysteresis).
 * Full ids are that same band: an extra screen of real cards, plus cards the
 * hysteresis band is still holding. Immediate ids (selection, drag, hover,
 * missing position) skip the mount queue.
 */
export function buildVisibleNodeSet({
  tasks,
  positionsToUse,
  nodeDimensions,
  container,
  containerRectRef,
  visibleBoundsRef,
  panRef,
  zoomRef,
  prevVisible,
  selectedNodes,
  draggingNode,
  connectingFrom,
  hoveredNodeId,
  isAnimatingLayout,
  isAnimatingIntoFocus,
  isAnimatingOutOfFocus,
}) {
  if (!container) return allTiers(tasks);

  let rect = containerRectRef.current;
  if (!rect?.width || !rect?.height) {
    const r = container.getBoundingClientRect();
    rect = { width: r.width, height: r.height };
    containerRectRef.current = rect;
  }

  if (isAnimatingLayout || isAnimatingIntoFocus || isAnimatingOutOfFocus) {
    return allTiers(tasks);
  }

  const pan = panRef.current;
  const z = zoomRef.current;
  const boundsScreen = calculateVisibleBounds(rect, pan, z, SCREEN_CARD_PADDING_RATIO);
  const boundsStrict = calculateVisibleBounds(rect, pan, z, VIEWPORT_PADDING_RATIO);
  const boundsLoose = calculateVisibleBounds(
    rect,
    pan,
    z,
    VIEWPORT_PADDING_RATIO + VIEWPORT_HYSTERESIS_EXTRA,
  );

  visibleBoundsRef.current = boundsStrict;

  if (!boundsStrict || !boundsLoose || !boundsScreen) return allTiers(tasks);

  const mounted = new Set();
  const full = new Set();
  const immediate = new Set();

  tasks.forEach((task) => {
    const position = positionsToUse[task.id];
    const pinned =
      selectedNodes.has(task.id) ||
      draggingNode === task.id ||
      connectingFrom === task.id ||
      hoveredNodeId === task.id;

    if (!position || pinned) {
      mounted.add(task.id);
      full.add(task.id);
      immediate.add(task.id);
      return;
    }

    const dims = nodeDimensions[task.id] || { width: NODE_WIDTH, height: NODE_HEIGHT };
    const onScreen = isNodeInBounds(position, dims, boundsScreen);
    const inStrict = isNodeInBounds(position, dims, boundsStrict);
    const inLoose = prevVisible.has(task.id) && isNodeInBounds(position, dims, boundsLoose);

    if (onScreen || inStrict || inLoose) mounted.add(task.id);
    // Cards the loose band is still holding stay real cards, not silhouettes.
    if (onScreen || inLoose) full.add(task.id);
  });

  return { mounted, full, immediate };
}
